import { FC, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { T } from 'src/shared/helpers';
import { RevealIcon } from 'src/components/widgets/icons/scene';
import { HeaderButton } from '../scene-editors/inspectorShell';
import { ZTooltip } from 'src/components/widgets/ztooltip';

// «DOVE STA?» ANCHE NEL COMPOSER (utente, 2026-10-15), come nella testata dell'Inspector della scena:
// l'icona in alto a destra porta alla riga nell'albero — apre i rami che la contengono, la porta in
// vista e la fa lampeggiare. L'Inspector e l'albero non si conoscono: si parlano con un evento, così
// nessuna delle due parti deve passare all'altra un riferimento attraverso la pagina.
const REVEAL_EVENT = 'zakeke:composer-reveal';

export const requestComposerReveal = (id: string) => window.dispatchEvent(new CustomEvent<string>(REVEAL_EVENT, { detail: id }));

/** Chi disegna l'albero ascolta qui: riceve l'id della riga da mostrare. */
export const useComposerRevealListener = (onReveal: (id: string) => void) => {
    const handler = useRef(onReveal);
    handler.current = onReveal;
    useEffect(() => {
        const listen = (event: Event) => handler.current((event as CustomEvent<string>).detail);
        window.addEventListener(REVEAL_EVENT, listen);
        return () => window.removeEventListener(REVEAL_EVENT, listen);
    }, []);
};

/**
 * LA RIGA TROVATA LAMPEGGIA: acceso e spento tre volte, come nello Scene Editor. Si aspetta un giro
 * di disegno perché i rami appena aperti abbiano messo la riga nella pagina.
 */
export const blinkComposerRow = (id: string) =>
    window.requestAnimationFrame(() =>
        window.requestAnimationFrame(() => {
            const row = document.querySelector<HTMLElement>(`[data-composer-row="${CSS.escape(id)}"]`);
            if (!row) return;
            row.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            // LO STESSO LAMPEGGIO DELLO SCENE EDITOR (TreeComponent, `blinkRow`): acceso per metà giro, spento per
            // l'altra metà, tre volte. Due soli fotogrammi con `steps` tenevano il colore acceso per tutto il giro.
            const hot = 'rgba(232, 101, 10, 0.4)';
            row.animate(
                [
                    { backgroundColor: hot, offset: 0 },
                    { backgroundColor: hot, offset: 0.499 },
                    { backgroundColor: 'transparent', offset: 0.5 },
                    { backgroundColor: 'transparent', offset: 1 }
                ],
                { duration: 360, iterations: 3 }
            );
        })
    );

// IL TASTO È QUELLO DELLA TESTATA DELLA SCENA (utente, 2026-10-15): stessa misura, stessa icona, stesso
// titolo. Con il bottone d'icona del Composer usciva più grande dell'originale.
const RevealSlot = styled.span`
    display: inline-flex;
    flex: none;
    align-self: start;
`;

export const ComposerRevealButton: FC<{ id: string }> = ({ id }) => (
    <RevealSlot>
        <ZTooltip title={T._('Show in the list', 'Admin')} position='bottom'>
            <HeaderButton type='button' aria-label={T._('Show in the list', 'Admin')} onClick={() => requestComposerReveal(id)}>
                <RevealIcon />
            </HeaderButton>
        </ZTooltip>
    </RevealSlot>
);
